import React from "react";
import { Alert, Button, Descriptions, Input, Modal, Space, Tag } from "antd";
import { CopyOutlined } from "@ant-design/icons";

import { notify } from "../../../../../services/toast.config";
import { WEBHOOK_SECRET_HEADER, fonadaSetupSteps, providerLabels } from "../../constants";

import type { WebhookIntegration, WebhookSecretSetup } from "../../types";

interface WebhookUrlModalProps {
  integration: WebhookIntegration | null;
  secretSetup: WebhookSecretSetup | null;
  onClose: () => void;
}

const copyToClipboard = (value: string, successMessage: string) => {
  navigator.clipboard
    .writeText(value)
    .then(() => notify.success(successMessage))
    .catch(() => notify.error("Failed to copy"));
};

interface CopyFieldProps {
  value: string;
  successMessage: string;
  isMonospace?: boolean;
}

const CopyField: React.FC<CopyFieldProps> = ({ value, successMessage, isMonospace }) => (
  <Space.Compact style={{ width: "100%" }}>
    <Input
      readOnly
      value={value}
      autoComplete="off"
      spellCheck={false}
      style={isMonospace ? { fontFamily: "monospace" } : undefined}
    />
    <Button icon={<CopyOutlined />} onClick={() => copyToClipboard(value, successMessage)}>
      Copy
    </Button>
  </Space.Compact>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="fw-semibold mb-2">{children}</div>
);

const SecretSetupSection: React.FC<{ secretSetup: WebhookSecretSetup }> = ({ secretSetup }) => (
  <div className="mb-3">
    <SectionLabel>Custom header</SectionLabel>
    <Descriptions bordered size="small" column={1} className="mb-2">
      <Descriptions.Item label="Header name">
        <CopyField value={secretSetup.headerName} successMessage="Header name copied" />
      </Descriptions.Item>
      <Descriptions.Item label="Header value">
        <CopyField value={secretSetup.headerValue} successMessage="Secret copied" isMonospace />
      </Descriptions.Item>
    </Descriptions>
    <Alert
      type="warning"
      showIcon
      message={
        secretSetup.isRegenerated
          ? "The previous URL and secret no longer work. Copy the new secret now — it will not be shown again."
          : "Copy this secret now — it will not be shown again."
      }
      description="Only a one-way hash is stored. If you lose the secret, use Regenerate URL & secret from the actions menu."
    />
  </div>
);

const AuthStatusSection: React.FC<{ integration: WebhookIntegration }> = ({ integration }) => (
  <div className="mb-3">
    <SectionLabel>Authentication</SectionLabel>
    <Descriptions bordered size="small" column={1}>
      <Descriptions.Item label="Method">
        Custom header (<code>{WEBHOOK_SECRET_HEADER}</code>)
      </Descriptions.Item>
      <Descriptions.Item label="Secret">
        {integration.secret_configured ? (
          <>
            <Tag color="green">Configured</Tag>
            <small className="text-muted">
              Hidden for security. Lost it? Use Regenerate URL & secret.
            </small>
          </>
        ) : (
          <>
            <Tag color="red">Missing</Tag>
            <small className="text-danger">
              All events are rejected. Use Regenerate URL & secret to secure this connection.
            </small>
          </>
        )}
      </Descriptions.Item>
    </Descriptions>
  </div>
);

const SetupSteps: React.FC = () => (
  <div className="p-3 rounded" style={{ background: "#f9fafb", border: "1px dashed #e5e7eb" }}>
    <SectionLabel>Setup steps</SectionLabel>
    <div className="d-flex flex-column gap-2">
      {fonadaSetupSteps.map((stepText, index) => (
        <div key={stepText} className="d-flex align-items-start gap-2 text-muted" style={{ fontSize: 13 }}>
          <span
            className="d-inline-flex align-items-center justify-content-center rounded-circle fw-semibold flex-shrink-0"
            style={{ width: 20, height: 20, fontSize: 11, background: "#e0f2fe", color: "#0369a1" }}>
            {index + 1}
          </span>
          <span>{stepText}</span>
        </div>
      ))}
    </div>
  </div>
);

const getTitle = (secretSetup: WebhookSecretSetup | null) => {
  if (!secretSetup) return "Webhook setup";
  return secretSetup.isRegenerated ? "New webhook URL & secret" : "Webhook created";
};

export const WebhookUrlModal: React.FC<WebhookUrlModalProps> = ({
  integration,
  secretSetup,
  onClose,
}) => {
  const providerName = integration ? providerLabels[integration.provider] : "";
  const isShowingSecret = Boolean(secretSetup);

  return (
    <Modal
      open={Boolean(integration)}
      title={getTitle(secretSetup)}
      onCancel={onClose}
      closable={!isShowingSecret}
      maskClosable={!isShowingSecret}
      keyboard={!isShowingSecret}
      destroyOnClose
      footer={
        <Button type="primary" onClick={onClose}>
          {isShowingSecret ? "I have copied the secret" : "Done"}
        </Button>
      }
      width={680}>
      {integration && (
        <>
          <p className="text-muted mb-3">
            {isShowingSecret
              ? `Add the URL and custom header below in your ${providerName} webhook settings for `
              : `Use this URL in your ${providerName} webhook settings for `}
            <span className="fw-semibold text-dark">{integration.name}</span>.
          </p>
          <div className="mb-3">
            <SectionLabel>Webhook URL</SectionLabel>
            <CopyField value={integration.webhook_url} successMessage="Webhook URL copied" />
          </div>
          {secretSetup ? (
            <SecretSetupSection secretSetup={secretSetup} />
          ) : (
            <AuthStatusSection integration={integration} />
          )}
          <SetupSteps />
        </>
      )}
    </Modal>
  );
};
